import { Box, Button, Card, CardActions, CardContent, Divider, FormControlLabel, Radio, RadioGroup, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, Checkbox, ListItemText, CircularProgress } from '@mui/material'
import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguageLocale } from '../../../../hooks/language-locale-hooks';
import { INPUT_DESCRIPTION, INPUT_ZONE_LIMIT, RESTRICT_SPEC_CHARACTER } from '../../../../constants';
import * as PXBColors from '@brightlayer-ui/colors';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import { Selectors } from '../../../../selectors';
import { AssetThunks } from '../../../../actions/thunks/assets-thunks';
import { SetIOConfigurationRequestBody, SetConfigFunction, SetIOConfig } from '../../../../api/types';

type Props = {

}
const optionTypographyStyle = {
    color: 'var(--primary-black-500, #424242)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 'normal',
};

const menuItemSx = {
    color: 'var(--primary-black-500, #424242)',
    fontSize: '16px',
    fontWeight: 600,
};

const menuProps = {
    PaperProps: {
        sx: optionTypographyStyle
    }
};

const SetInputs = (props: Props) => {
    const { t } = useLanguageLocale();
    const dispatch = useDispatch();
    const assetData = useSelector(Selectors.Asset.assets);
    const ioConfigData = assetData.getIoConfigDetails;
    const isSetIOConfigSaving = assetData.setIOConfigurationLoading;

    const [inputName, setInputName] = useState("")
    const [inputNameOptional, setInputNameOptional] = useState("")
    const [description, setDescription] = useState("")
    const [descriptionOptional, setDescriptionOptional] = useState("")
    const [connectionMode, setConnectionMode] = useState("notconfigured")
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [selectedFunction, setSelectedFunction] = useState("restOrder")
    const [dialogOriginalState, setDialogOriginalState] = useState<any>(null)

    // Track unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalValues, setOriginalValues] = useState<any>(null);

    // Error tracking for S1/S2 fields
    const [inputNameError, setInputNameError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [inputNameTouched, setInputNameTouched] = useState(false);
    const [descriptionTouched, setDescriptionTouched] = useState(false);

    // Use useMemo to prevent recreation on every render
    const zoneOptions = React.useMemo(() => Array.from({ length: 16 }, (_, i) => `Zone ${i + 1}`), []);
    const allOption = t('FILTER.ALL');
    const allOptions = React.useMemo(() => [allOption, ...zoneOptions], [allOption, zoneOptions]);

    const [maintainedModeValue, setMaintainedModeValue] = useState<string[]>([]);
    const [iaModeValue, setIaModeValue] = useState<string[]>([]);
    const [functionalTestValue, setFunctionalTestValue] = useState<string[]>([]);

    // Track if initial data has been loaded
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

    // Reset initialization when whitelistId changes
    useEffect(() => {
        setIsInitialLoadComplete(false);
        setOriginalValues(null);
        setHasUnsavedChanges(false);
    }, [ioConfigData.data?.whitelistId]);

    // Bind s1s2Name and s1s2Description from API response to state
    useEffect(() => {
        // Set unconditionally, falling back to empty strings to avoid stale values
        setInputName(ioConfigData.data?.s1s2Name ?? "");
        setDescription(ioConfigData.data?.s1s2Description ?? "");

        // Bind inputConfig data (name and description)
        if (ioConfigData.data?.inputConfig && ioConfigData.data.inputConfig.length > 0) {
            const firstInputConfig = ioConfigData.data.inputConfig[0];

            // Set unconditionally, falling back to empty strings
            setInputNameOptional(firstInputConfig.name ?? "");
            setDescriptionOptional(firstInputConfig.description ?? "");
            // Bind connectionMode: 0 = notconfigured, 1 = open, 2 = closed
            if (firstInputConfig.connectionMode !== undefined) {
                const modeMap: { [key: number]: string } = {
                    0: 'notconfigured',
                    1: 'open',
                    2: 'closed'
                };
                setConnectionMode(modeMap[firstInputConfig.connectionMode] || 'notconfigured');
            }

            // Bind configFunctions - find which function has status: true and bind values
            if (firstInputConfig.configFunctions && firstInputConfig.configFunctions.length > 0) {
                firstInputConfig.configFunctions.forEach((func) => {
                    let radioValue: string | undefined;

                    // Map by function name for stability
                    const funcName = func.name?.toLowerCase();
                    if (funcName?.includes('rest order')) {
                        radioValue = "restOrder";
                    } else if (funcName?.includes('switch maintained mode off')) {
                        radioValue = "switchMaintained";
                    } else if (funcName?.includes('active ia mode') || funcName?.includes('activate ia mode')) {
                        radioValue = "activateIA";
                    } else if (funcName?.includes('start functional test')) {
                        radioValue = "startFunctional";
                    } else if (funcName?.includes('stop functional') || funcName?.includes('duration test')) {
                        radioValue = "stopTest";
                    } else if (funcName?.includes('external failure')) {
                        radioValue = "externalFailure";
                    }

                    // Set the selected function based on status: true
                    if (func.status === true && radioValue) {
                        setSelectedFunction(radioValue);
                    }

                    // Bind values to corresponding dropdowns based on function name
                    if (func.values && func.values.length > 0) {
                        let mappedValues: string[];

                        if (func.values.includes("All")) {
                            // If "All" is in values, select all zones
                            mappedValues = [allOption, ...zoneOptions];
                        } else {
                            // Map numeric or string values to zone strings (e.g., [1, 4] or ["1", "4"] -> ["Zone 1", "Zone 4"])
                            mappedValues = func.values.map((val: any) => {
                                if (typeof val === 'number') {
                                    return `Zone ${val}`;
                                } else if (typeof val === 'string' && !isNaN(Number(val))) {
                                    // Convert string numbers to Zone format
                                    return `Zone ${val}`;
                                }
                                return val;
                            });
                        }

                        // Map by function name instead of index
                        if (funcName?.includes('switch maintained mode off')) {
                            setMaintainedModeValue(mappedValues);
                        } else if (funcName?.includes('active ia mode') || funcName?.includes('activate ia mode')) {
                            setIaModeValue(mappedValues);
                        } else if (funcName?.includes('start functional test')) {
                            setFunctionalTestValue(mappedValues);
                        }
                    } else {
                        // If values array is empty, default to "All" based on function name
                        if (funcName?.includes('switch maintained mode off')) {
                            setMaintainedModeValue([allOption, ...zoneOptions]);
                        } else if (funcName?.includes('active ia mode') || funcName?.includes('activate ia mode')) {
                            setIaModeValue([allOption, ...zoneOptions]);
                        } else if (funcName?.includes('start functional test')) {
                            setFunctionalTestValue([allOption, ...zoneOptions]);
                        }
                    }
                });
            }
        }

        // After all data binding is complete, mark as loaded and store original values
        if (ioConfigData.data && !isInitialLoadComplete) {
            setIsInitialLoadComplete(true);
        }
    }, [ioConfigData, allOption, zoneOptions, isInitialLoadComplete]);

    // Store original values AFTER initial data load is complete
    useEffect(() => {
        if (isInitialLoadComplete && !originalValues) {
            setOriginalValues({
                inputName,
                inputNameOptional,
                description,
                descriptionOptional,
                connectionMode,
                selectedFunction,
                maintainedModeValue: [...maintainedModeValue],
                iaModeValue: [...iaModeValue],
                functionalTestValue: [...functionalTestValue]
            });
        }
    }, [isInitialLoadComplete, inputName, inputNameOptional, description, descriptionOptional, connectionMode, selectedFunction, maintainedModeValue, iaModeValue, functionalTestValue, originalValues]);

    // Check for changes and validate fields whenever any field updates
    useEffect(() => {
        // Validate S1/S2 fields only if touched
        const isInputNameEmpty = inputName.trim() === '';
        const isDescriptionEmpty = description.trim() === '';

        setInputNameError(inputNameTouched && isInputNameEmpty);
        setDescriptionError(descriptionTouched && isDescriptionEmpty);

        if (!originalValues) {
            setHasUnsavedChanges(false);
            return;
        }

        // Helper function to compare zones only for the selected function
        const getZonesChanged = (): boolean => {
            // If selected function changed, it's a change
            if (selectedFunction !== originalValues.selectedFunction) {
                return true;
            }

            // Only compare zones for the currently selected function
            switch (selectedFunction) {
                case 'switchMaintained':
                    return JSON.stringify([...maintainedModeValue].sort()) !== JSON.stringify([...originalValues.maintainedModeValue].sort());
                case 'activateIA':
                    return JSON.stringify([...iaModeValue].sort()) !== JSON.stringify([...originalValues.iaModeValue].sort());
                case 'startFunctional':
                    return JSON.stringify([...functionalTestValue].sort()) !== JSON.stringify([...originalValues.functionalTestValue].sort());
                default:
                    // For functions without zones (restOrder, stopTest, externalFailure), no zone comparison needed
                    return false;
            }
        };

        const hasChanges =
            inputName.trim() !== (originalValues.inputName || '').trim() ||
            inputNameOptional.trim() !== (originalValues.inputNameOptional || '').trim() ||
            description.trim() !== (originalValues.description || '').trim() ||
            descriptionOptional.trim() !== (originalValues.descriptionOptional || '').trim() ||
            connectionMode !== originalValues.connectionMode ||
            getZonesChanged();

        // Only show save button if there are changes AND no validation errors
        setHasUnsavedChanges(hasChanges && !isInputNameEmpty && !isDescriptionEmpty);
    }, [inputName, inputNameOptional, description, descriptionOptional, connectionMode, selectedFunction, maintainedModeValue, iaModeValue, functionalTestValue, originalValues, inputNameTouched, descriptionTouched]);


    function handleZoneChange(setter: React.Dispatch<React.SetStateAction<string[]>>, value: string | string[]) {
        let valArr = typeof value === 'string' ? value.split(',') : value;
        valArr = Array.from(new Set(valArr));
        setter((previousSelected) => {
            const hadAllPreviously = previousSelected.includes(allOption);
            const hasAllNow = valArr.includes(allOption);

            if (!hadAllPreviously && hasAllNow) {
                return [allOption, ...zoneOptions];
            }

            if (hadAllPreviously && !hasAllNow && valArr.length === zoneOptions.length) {
                return [];
            }

            if (hasAllNow) {
                if (valArr.length === 1 || valArr.length === zoneOptions.length + 1) {
                    return [allOption, ...zoneOptions];
                }
                return valArr.filter(v => v !== allOption);
            }

            if (valArr.length === zoneOptions.length) {
                return [allOption, ...zoneOptions];
            }

            if (valArr.length === 0) {
                return [];
            }

            return valArr;
        });
    }

    function getZoneLabel(selected: string[]) {
        if (selected.includes(allOption)) return allOption;
        if (selected.length === 1) return '1 zone';
        if (selected.length > 1) return `${selected.length} zones`;
        return '';
    }

    const handleInputName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setInputNameTouched(true);
        if (e.target.value === '' || RESTRICT_SPEC_CHARACTER.test(e.target.value)) {
            setInputName(e.target.value)
        }
    }

    const handleInputOptional = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (e.target.value === '' || RESTRICT_SPEC_CHARACTER.test(e.target.value)) {
            setInputNameOptional(e.target.value)
        }
    }

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setDescriptionTouched(true); if (e.target.value === '' || RESTRICT_SPEC_CHARACTER.test(e.target.value)) {
            setDescription(e.target.value)
        }
    }

    const handleDescriptionOptional = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (e.target.value === '' || RESTRICT_SPEC_CHARACTER.test(e.target.value)) {
            setDescriptionOptional(e.target.value)
        }
    }

    const handleOpenDialog = (): void => {
        // Save current state before opening dialog
        setDialogOriginalState({
            selectedFunction,
            maintainedModeValue: [...maintainedModeValue],
            iaModeValue: [...iaModeValue],
            functionalTestValue: [...functionalTestValue]
        });
        setIsDialogOpen(true)
    }

    const handleCloseDialog = (): void => {
        // Reset to state before dialog was opened
        if (dialogOriginalState) {
            setSelectedFunction(dialogOriginalState.selectedFunction);
            setMaintainedModeValue(dialogOriginalState.maintainedModeValue);
            setIaModeValue(dialogOriginalState.iaModeValue);
            setFunctionalTestValue(dialogOriginalState.functionalTestValue);
        }
        setIsDialogOpen(false)
    }

    const handleSave = (): void => {        // Mark fields as touched
        if (isSetIOConfigSaving) {
            return;
        }

        // Mark fields as touched
        setInputNameTouched(true);
        setDescriptionTouched(true);
        // Validate fields are not empty
        if (inputName.trim() === '' || description.trim() === '') {
            return;
        }

        // Validate that there are actual changes
        if (!hasUnsavedChanges) {
            return;
        }

        // Prepare the request body
        const whitelistId = ioConfigData.data?.whitelistId || '';

        // Map connection mode back to number
        const connectionModeMap: { [key: string]: number } = {
            'notconfigured': 0,
            'open': 1,
            'closed': 2
        };

        // Helper function to map zone strings back to numbers
        const mapZonesToNumbers = (zones: string[]): (string | number)[] => {
            // If the "All" option is selected, preserve it for the backend
            if (zones.includes(allOption)) {
                return ['All'];
            }

            return zones.map(z => {
                const match = z.match(/Zone (\d+)/);
                return match ? parseInt(match[1]) : z;
            });
        };

        // Build configFunctions array based on selected function
        const existingConfigFunctions = ioConfigData.data?.inputConfig?.[0]?.configFunctions;

        const getConfigFunctionName = (index: number, fallbackName: string): string => {
            const existingName = existingConfigFunctions?.[index]?.name;
            return typeof existingName === 'string' && existingName.length > 0 ? existingName : fallbackName;
        };

        const configFunctions: SetConfigFunction[] = [
            {
                name: getConfigFunctionName(0, "Rest Order"),
                status: selectedFunction === "restOrder" ? 1 : 0,
                values: []
            },
            {
                name: getConfigFunctionName(1, "Switch maintained mode OFF for"),
                status: selectedFunction === "switchMaintained" ? 1 : 0,
                values: selectedFunction === "switchMaintained" ? mapZonesToNumbers(maintainedModeValue) : []
            },
            {
                name: getConfigFunctionName(2, "Active IA mode for"),
                status: selectedFunction === "activateIA" ? 1 : 0,
                values: selectedFunction === "activateIA" ? mapZonesToNumbers(iaModeValue) : []
            },
            {
                name: getConfigFunctionName(3, "Start functional test"),
                status: selectedFunction === "startFunctional" ? 1 : 0,
                values: selectedFunction === "startFunctional" ? mapZonesToNumbers(functionalTestValue) : []
            },
            {
                name: getConfigFunctionName(4, "Stop functional/duration test"),
                status: selectedFunction === "stopTest" ? 1 : 0,
                values: []
            },
            {
                name: getConfigFunctionName(5, "External failure"),
                status: selectedFunction === "externalFailure" ? 1 : 0,
                values: []
            }
        ];

        // Build IO config for Input 1
        const ioconfigs: SetIOConfig[] = [
            {
                id: ioConfigData.data?.inputConfig?.[0]?.id || 1,
                iotype: 1, // 1 for input
                name: inputNameOptional.trim(),
                description: descriptionOptional.trim(),
                connectionMode: connectionModeMap[connectionMode],
                configFunctions: configFunctions,
                bitValue: ioConfigData.data?.inputConfig?.[0]?.bitValue
            }
        ];

        const requestBody: SetIOConfigurationRequestBody = {
            whitelistId,
            s1s2Name: inputName.trim(),
            s1s2Description: description.trim(),
            s1s2Status: ioConfigData.data?.s1s2Status || false,
            ioconfigs
        };

        // Dispatch the thunk
        dispatch(AssetThunks.setIOConfiguration(requestBody));

        // Update original values to current values (trimmed)
        setOriginalValues({
            inputName: inputName.trim(),
            inputNameOptional: inputNameOptional.trim(),
            description: description.trim(),
            descriptionOptional: descriptionOptional.trim(),
            connectionMode,
            selectedFunction,
            maintainedModeValue: [...maintainedModeValue],
            iaModeValue: [...iaModeValue],
            functionalTestValue: [...functionalTestValue]
        });

        setHasUnsavedChanges(false);
    };

    const handleCancel = (): void => {
        // Revert to original values
        if (originalValues) {
            setInputName(originalValues.inputName);
            setInputNameOptional(originalValues.inputNameOptional);
            setDescription(originalValues.description);
            setDescriptionOptional(originalValues.descriptionOptional);
            setConnectionMode(originalValues.connectionMode);
            setSelectedFunction(originalValues.selectedFunction);
            setMaintainedModeValue(originalValues.maintainedModeValue);
            setIaModeValue(originalValues.iaModeValue);
            setFunctionalTestValue(originalValues.functionalTestValue);
        }
        setHasUnsavedChanges(false);
    };

    const handleFinish = (): void => {
        // Clear dialog state as changes are accepted
        setDialogOriginalState(null);
        setIsDialogOpen(false)
    }

    return (
        <>
            <Box data-testid="set-input-container"
                style={{
                    marginRight: '8%',
                    marginLeft: '5%',
                    marginTop: '1%',
                    width: "140%",
                    position: 'relative'
                }}>

                {isSetIOConfigSaving && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1300,
                            backgroundColor: 'rgba(255, 255, 255, 0.35)'
                        }}
                        data-testid="set-io-save-progress-overlay"
                    >
                        <CircularProgress size={44} color="primary" data-testid="set-io-save-progress" />
                    </Box>
                )}

                {/* Save/Cancel Buttons - Sticky at top */}
                {(hasUnsavedChanges || isSetIOConfigSaving) && (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 100,
                            backgroundColor: PXBColors.blue[50],
                            padding: '12px 16px',
                            marginBottom: 2,
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                    >
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color={PXBColors.blue[700]}>
                                    {isSetIOConfigSaving
                                        ? (t('COMMON.LOADING') || 'Saving...')
                                        : (t('ASSETS_DETAILS.ZONES_CONFIGURATION_UNSAVED_CHANGES') || 'You have unsaved changes')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleCancel}
                                        disabled={isSetIOConfigSaving}
                                        data-testid="cancel-button-sticky"
                                    >
                                        {t('COMMON.CANCEL') || 'Cancel'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSave}
                                        disabled={isSetIOConfigSaving}
                                        data-testid="save-button-sticky"
                                    >
                                        {t('COMMON.SAVE') || 'Save'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                <Typography sx={{ display: 'flex', gap: '1%' }} fontWeight={400} data-testid='inputs-description'>
                    <InfoOutlinedIcon /> {t('ASSETS_DETAILS.SET_INPUT_DESCRIPTION')}
                </Typography>

                <Card data-testid="input-field-container" style={{ flex: '1 1 0px', overflow: 'auto', marginTop: '2%', }} >
                    <CardContent>
                        <Typography fontSize={20} fontWeight={600} data-testid='lumns-config-title'>
                            {t('DRY_CONTACT.S1_S2')} {t('DUALSMART.DS_INPUTS')}
                        </Typography>
                        <Typography variant="subtitle1" fontSize={16} fontWeight={400} data-testid='lumns-config-subtitle'>
                            {t('ASSETS_DETAILS.BLOCK_THE_SYSTEM')}
                        </Typography>
                        <Box style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '5%',
                            justifyContent: 'start',
                            marginTop: '2%'
                        }}>
                            <Box style={{ width: '100%' }}>
                                <TextField
                                    id='InputName'
                                    inputProps={{ 'data-testid': 'InputName', maxLength: INPUT_ZONE_LIMIT }}
                                    variant="filled" fullWidth
                                    placeholder={t('ASSETS_DETAILS.PLACEHOLDER_TEXT')}
                                    label={t('ASSETS_DETAILS.SET_INPUT_NAME')}
                                    value={inputName}
                                    onChange={handleInputName}
                                    error={inputNameError}
                                    helperText={inputNameError ? t('CONFIG_IO.S1S2_INPUT_NAME_ERROR') : ''}
                                />
                                <div data-testid='InputNameHelperTextId'
                                    style={{ height: 17, width: '100%', color: PXBColors.gray[500] }}>
                                    <small className="form-text" style={{ width: '85%', float: 'left', paddingLeft: '2%' }}>
                                        {!inputNameError && t('ORGANIZATION_MANAGE.ORGANIZATION_NAME_HELPER')}</small>
                                    <Typography data-testid='InputNameLimitId' style={{ width: 'auto', float: 'right', fontSize: 12, color: PXBColors.gray[500] }}>
                                        {inputName.length || 0}/{INPUT_ZONE_LIMIT}
                                    </Typography>
                                </div>
                            </Box>
                            <Box style={{ width: '100%' }}>
                                <TextField
                                    id="DescriptionField"
                                    inputProps={{ 'data-testid': 'input-description-field', maxLength: INPUT_DESCRIPTION }}
                                    variant="filled" fullWidth
                                    label={t('LOGBOOK_DETAILS.DESCRIPTION')}
                                    placeholder={t('ASSETS_DETAILS.PLACEHOLDER_TEXT')}
                                    value={description}
                                    onChange={handleDescriptionChange}
                                    error={descriptionError}
                                    helperText={descriptionError ? t('CONFIG_IO.S1S2_DESCRIPTION_ERROR') : ''}
                                />
                                <div data-testid='DescriptionHelperTextId'
                                    style={{ height: 17, width: '100%', color: PXBColors.gray[500] }}>
                                    <small className="form-text" style={{ width: '85%', float: 'left', paddingLeft: '2%' }}>
                                        {!descriptionError && t('ORGANIZATION_MANAGE.ORGANIZATION_NAME_HELPER')}</small>
                                    <Typography data-testid='DescriptionCharLengthId' style={{ width: 'auto', float: 'right', fontSize: 12, color: PXBColors.gray[500] }}>
                                        {description.length || 0}/{INPUT_DESCRIPTION}
                                    </Typography>
                                </div>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card data-testid="optional-input-field-container" style={{ flex: '1 1 0px', overflow: 'auto', marginTop: '5%' }} >
                    <CardContent>
                        <Typography fontSize={20} fontWeight={600} data-testid='optional-input-title'>
                            {t('DRY_CONTACT.INPUT_1')}
                        </Typography>
                        <Typography variant="subtitle1" fontSize={16} sx={{ marginBottom: '2' }} fontWeight={400} data-testid='optional-input-subtitle'>
                            {t('ASSETS_DETAILS.OPTIONAL_INPUTS')}
                        </Typography>
                        <Box style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '5%',
                            justifyContent: 'start',
                            marginTop: '2%',
                        }}>
                            <Box style={{ width: '100%' }}>
                                <TextField
                                    id='OptionalInputName'
                                    inputProps={{ 'data-testid': 'OptionalInputName', maxLength: INPUT_ZONE_LIMIT }}
                                    variant="filled" fullWidth
                                    placeholder={t('ASSETS_DETAILS.PLACEHOLDER_TEXT')}
                                    label={t('ASSETS_DETAILS.SET_INPUT_NAME')}
                                    value={inputNameOptional}
                                    onChange={handleInputOptional}
                                />
                                <div data-testid='OptionalInputNameHelperTextId'
                                    style={{ height: 17, width: '100%', color: PXBColors.gray[500] }}>
                                    <small className="form-text" style={{ width: '85%', float: 'left', paddingLeft: '2%' }}>
                                        {t('ORGANIZATION_MANAGE.ORGANIZATION_NAME_HELPER')}</small>
                                    <Typography data-testid='OptionalInputNameLimitId' style={{ width: 'auto', float: 'right', fontSize: 12, color: PXBColors.gray[500] }}>
                                        {inputNameOptional.length || 0}/{INPUT_ZONE_LIMIT}
                                    </Typography>
                                </div>
                            </Box>
                            <Box style={{ width: '100%' }}>
                                <TextField
                                    id="DescriptionFieldOptional"
                                    inputProps={{ 'data-testid': 'DescriptionFieldOptional', maxLength: INPUT_DESCRIPTION }}
                                    variant="filled" fullWidth
                                    label={t('LOGBOOK_DETAILS.DESCRIPTION')}
                                    placeholder={t('ASSETS_DETAILS.PLACEHOLDER_TEXT')}
                                    value={descriptionOptional}
                                    onChange={handleDescriptionOptional}
                                />
                                <div data-testid='OptionalDescriptionHelperTextId'
                                    style={{ height: 17, width: '100%', color: PXBColors.gray[500] }}>
                                    <small className="form-text" style={{ width: '85%', float: 'left', paddingLeft: '2%' }}>
                                        {t('ORGANIZATION_MANAGE.ORGANIZATION_NAME_HELPER')}</small>
                                    <Typography data-testid='OptionalDescriptionCharLengthId' style={{ width: 'auto', float: 'right', fontSize: 12, color: PXBColors.gray[500] }}>
                                        {descriptionOptional.length || 0}/{INPUT_DESCRIPTION}
                                    </Typography>
                                </div>
                            </Box>
                        </Box>
                        <Box sx={{ marginTop: 5 }} data-testid="input-radio-button">
                            <Typography sx={{ display: 'flex', gap: '1%' }} fontSize={16} fontWeight={600} data-testid='communication-method-title'>
                                {t('ASSETS_DETAILS.COMMUNICATION_METHOD')} <HelpOutlineIcon />
                            </Typography>
                            <RadioGroup
                                name="controlled-radio-buttons-group"
                                data-testid="communication-input-radio"
                                style={{ marginTop: '15px' }}
                                value={connectionMode}
                                onChange={(e) => setConnectionMode(e.target.value)}
                            >
                                <FormControlLabel data-testid="input-not-configure" style={{ fontSize: '14px', margin: 0, padding: 0, color: '#424E54', fontWeight: 400 }} value="notconfigured" control={<Radio />} label={t('DRY_CONTACT.IOSTATUS.NOT_CONFIGURED')} />
                                <FormControlLabel data-testid="input-open" style={{ fontSize: '14px', margin: 0, padding: 0, color: '#424E54', fontWeight: 400 }} value="open" control={<Radio />} label={t('DRY_CONTACT.NORMALLY_OPEN')} />
                                <FormControlLabel data-testid="input-closed" style={{ fontSize: '14px', margin: 0, padding: 0, color: '#424E54', fontWeight: 400 }} value="closed" control={<Radio />} label={t('DRY_CONTACT.NORMALLY_CLOSED')} />
                            </RadioGroup>
                        </Box>
                        <Divider />
                    </CardContent>
                    <CardActions data-testid="configure-function" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Button sx={{ width: '50%' }} variant='outlined' startIcon={<EditIcon />} data-testid='configure-input-functions-button' onClick={handleOpenDialog}>
                            {t('ASSETS_DETAILS.CONFIGURE_INPUT_FUNCTION')}
                        </Button>
                    </CardActions>
                </Card>

            </Box>

            {/* Input Function Dialog */}
            <Dialog
                open={isDialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                data-testid="input-function-dialog"
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight={600}>
                        {t('CONFIG_IO.INPUT_1_FUNCTION')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <RadioGroup
                        value={selectedFunction}
                        onChange={(e) => setSelectedFunction(e.target.value)}
                        data-testid="function-radio-group"
                    >

                        <FormControlLabel
                            value="restOrder"
                            control={<Radio />}
                            label={<span style={optionTypographyStyle}>{t('CONFIG_IO.REST_ORDER')}</span>}
                            sx={{ marginBottom: 2 }}
                        />

                        <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                            <FormControlLabel
                                value="switchMaintained"
                                control={<Radio />}
                                label={<span style={optionTypographyStyle}>{t('CONFIG_IO.SWITCH_MAINTAINED_MODE_OFF_FOR')}</span>}
                                sx={{ flex: 1, marginRight: 0 }}
                            />
                            <Select
                                multiple
                                value={maintainedModeValue}
                                onChange={(e) => handleZoneChange(setMaintainedModeValue, e.target.value)}
                                size="small"
                                sx={{ minWidth: 180 }}
                                disabled={selectedFunction !== 'switchMaintained'}
                                MenuProps={menuProps}
                                renderValue={(selected) => getZoneLabel(selected as string[])}
                            >
                                {allOptions.map((zone) => (
                                    <MenuItem key={zone} value={zone} sx={menuItemSx}>
                                        <Checkbox
                                            checked={maintainedModeValue.indexOf(zone) > -1}
                                        />
                                        <ListItemText primary={zone} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                            <FormControlLabel
                                value="activateIA"
                                control={<Radio />}
                                label={<span style={optionTypographyStyle}>{t('CONFIG_IO.ACTIVATE_IA_MODE_FOR')}</span>}
                                sx={{ flex: 1, marginRight: 0 }}
                            />
                            <Select
                                multiple
                                value={iaModeValue}
                                onChange={(e) => handleZoneChange(setIaModeValue, e.target.value)}
                                size="small"
                                sx={{ minWidth: 180 }}
                                disabled={selectedFunction !== 'activateIA'}
                                MenuProps={menuProps}
                                renderValue={(selected) => getZoneLabel(selected as string[])}
                            >
                                {allOptions.map((zone) => (
                                    <MenuItem key={zone} value={zone} sx={menuItemSx}>
                                        <Checkbox
                                            checked={iaModeValue.indexOf(zone) > -1}
                                        />
                                        <ListItemText primary={zone} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                            <FormControlLabel
                                value="startFunctional"
                                control={<Radio />}
                                label={<span style={optionTypographyStyle}>{t('CONFIG_IO.START_FUNCTIONAL_TEST')}</span>}
                                sx={{ flex: 1, marginRight: 0 }}
                            />
                            <Select
                                multiple
                                value={functionalTestValue}
                                onChange={(e) => handleZoneChange(setFunctionalTestValue, e.target.value)}
                                size="small"
                                sx={{ minWidth: 180 }}
                                disabled={selectedFunction !== 'startFunctional'}
                                MenuProps={menuProps}
                                renderValue={(selected) => getZoneLabel(selected as string[])}
                            >
                                {allOptions.map((zone) => (
                                    <MenuItem key={zone} value={zone} sx={menuItemSx}>
                                        <Checkbox
                                            checked={functionalTestValue.indexOf(zone) > -1}
                                        />
                                        <ListItemText primary={zone} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <FormControlLabel
                            value="stopTest"
                            control={<Radio />}
                            label={<span style={optionTypographyStyle}>{t('CONFIG_IO.START_FUNCTIONAL_TEST_OR_DURATION_TEST')}</span>}
                            sx={{ marginBottom: 2 }}
                        />

                        <FormControlLabel
                            value="externalFailure"
                            control={<Radio />}
                            label={<span style={optionTypographyStyle}>{t('CONFIG_IO.EXTERNAL_FAILURE')}</span>}
                        />
                    </RadioGroup>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: '24px',
                        borderTop: '1px solid #e0e0e0',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        sx={{
                            color: '#d32f2f',
                            borderColor: '#d32f2f',
                            '&:hover': {
                                borderColor: '#d32f2f',
                                backgroundColor: 'rgba(211, 47, 47, 0.04)'
                            }
                        }}
                        data-testid="cancel-button"
                    >
                        {t('COMMON.CANCEL')}
                    </Button>
                    <Box sx={{ width: '289px' }} />
                    <Button
                        onClick={handleFinish}
                        variant="contained"
                        startIcon={<CheckIcon />}
                        sx={{
                            backgroundColor: PXBColors.blue[500],
                            '&:hover': {
                                backgroundColor: PXBColors.blue[700]
                            }
                        }}
                        data-testid="finish-button"
                    >
                        {t('ACTIONS.FINISH')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default SetInputs
